<template>
  <b-modal ref="modalUnirSap" size="lg" centered title="Unir QMA com casa do SAP" hide-footer>
    <div v-if="unir.casas.length">
      <!-- Vínculo atual do QMA: aparece primeiro, com "−" pra desvincular. -->
      <div v-if="casaVinculada" class="vinculo-atual">
        <div class="vinculo-info">
          <div class="casa-main">
            {{ casaVinculada.empreendimento }}
            <span class="badge badge-success ml-1">vinculada</span>
          </div>
          <div class="casa-sub">
            Obra {{ casaVinculada.obraRefSap }} · {{ casaVinculada.unidade || '—' }}<span v-if="casaVinculada.tipoImovel"> · {{ casaVinculada.tipoImovel }}</span><span v-if="casaVinculada.cliente"> · {{ casaVinculada.cliente }}</span>
          </div>
        </div>
        <button class="btn-remover" title="Desvincular" @click="desvincular">−</button>
      </div>

      <div class="filtros-casa mb-2">
        <select v-model="filtro.empreendimento" class="form-control form-control-sm mb-2" @change="paginaAtual = 1">
          <option value="">Todos os empreendimentos</option>
          <option v-for="emp in empreendimentos" :key="emp" :value="emp">{{ emp }}</option>
        </select>
        <div class="filtros-texto">
          <b-form-input v-model="filtro.unidade" placeholder="Unidade" autocomplete="off" @input="paginaAtual = 1"></b-form-input>
          <b-form-input v-model="filtro.resto" placeholder="Obra, Tipo, Cliente..." autocomplete="off" @input="paginaAtual = 1"></b-form-input>
        </div>
      </div>

      <div class="text-muted mb-1" style="font-size: 12px;">
        {{ casasFiltradas.length }} casa{{ casasFiltradas.length === 1 ? '' : 's' }} encontrada{{ casasFiltradas.length === 1 ? '' : 's' }}
      </div>

      <div class="lista-casas">
        <div v-if="!casasFiltradas.length" class="text-muted p-2 text-center">
          Nenhuma casa com esse filtro.
        </div>
        <div
          v-for="casa in casasPagina"
          :key="casa._key"
          class="casa-row"
          :class="{ 'casa-row-sel': casaSel && casaSel._key === casa._key }"
          @click="casaSel = casa"
        >
          <div class="casa-main">
            {{ casa.empreendimento }}
            <span v-if="casa.jaVinculada" class="badge badge-secondary ml-1">já unida</span>
          </div>
          <div class="casa-sub">
            Obra {{ casa.obraRefSap }} · {{ casa.unidade || '—' }}<span v-if="casa.tipoImovel"> · {{ casa.tipoImovel }}</span><span v-if="casa.cliente"> · {{ casa.cliente }}</span>
          </div>
        </div>
      </div>

      <b-pagination
        v-if="casasFiltradas.length > porPagina"
        v-model="paginaAtual"
        :total-rows="casasFiltradas.length"
        :per-page="porPagina"
        align="center"
        size="sm"
        class="mt-2 mb-0"
      ></b-pagination>

      <div class="text-right mt-2">
        <b-button variant="secondary" class="mr-2" @click="$refs.modalUnirSap.hide()">Cancelar</b-button>
        <b-button variant="success" :disabled="!casaSel" @click="confirmarUnir">Unir</b-button>
      </div>
    </div>
    <div v-else class="text-muted">Nenhuma casa encontrada no SAP para a obra deste QMA.</div>
  </b-modal>
</template>

<script>
import ObraVinculoSapRepository from '@/shared/http/repositories/business/obraVinculoSap'
import { loading, toast } from '@/shared/utils/UIHelper'

export default {
  name: 'modalUnirSap',
  data () {
    return {
      qmaId: null,
      filtro: { empreendimento: '', unidade: '', resto: '' },
      paginaAtual: 1,
      porPagina: 8,
      casaSel: null,
      unir: {
        casas: []
      }
    }
  },
  computed: {
    // Casa atualmente ligada a este QMA (marcada pelo backend).
    casaVinculada () {
      return this.unir.casas.find(c => c.vinculadaNesteQma) || null
    },
    // Empreendimento: combo com todos os valores distintos das casas carregadas.
    empreendimentos () {
      const vistos = new Set()
      const lista = []
      this.unir.casas.forEach(c => {
        if (c.empreendimento && !vistos.has(c.empreendimento)) {
          vistos.add(c.empreendimento)
          lista.push(c.empreendimento)
        }
      })
      return lista.sort((a, b) => a.localeCompare(b))
    },
    // Empreendimento = combo (igual exato). Unidade = campo próprio à esquerda (bate
    // unidade OU quadra, já que o id exibido junta os dois). Obra/Tipo/Cliente voltam a
    // ser 1 campo só ("resto") — qualquer um dos 3 bate. A vinculada sai da lista.
    casasFiltradas () {
      const contem = (valor, termo) => (valor || '').toLowerCase().indexOf(termo) !== -1
      const unidade = this.filtro.unidade.trim().toLowerCase()
      const resto = this.filtro.resto.trim().toLowerCase()
      return this.unir.casas.filter(c =>
        !c.vinculadaNesteQma &&
        (!this.filtro.empreendimento || c.empreendimento === this.filtro.empreendimento) &&
        (!unidade || contem(c.unidade, unidade) || contem(c.quadra, unidade)) &&
        (!resto || contem(c.obraRefSap, resto) || contem(c.tipoImovel, resto) || contem(c.cliente, resto)))
    },
    casasPagina () {
      const ini = (this.paginaAtual - 1) * this.porPagina
      return this.casasFiltradas.slice(ini, ini + this.porPagina)
    }
  },
  methods: {
    // Abre o modal para um QMA da lista.
    open (qmaId) {
      if (!qmaId) { toast.error('QMA não identificado.'); return }
      this.qmaId = qmaId
      this.filtro = { empreendimento: '', unidade: '', resto: '' }
      this.paginaAtual = 1
      this.casaSel = null
      this.unir.casas = []
      this.carregar(() => this.$refs.modalUnirSap.show())
    },
    // Busca as casas do SAP da obra do QMA; o server resolve obra->referência pelo id.
    carregar (onOk) {
      loading.push()
      ObraVinculoSapRepository.GetCasasSapPorQma(this.qmaId)
        .then(response => {
          const casas = (response.data && response.data.data) ? response.data.data : []
          // _key: identidade estável pra seleção sobreviver a filtro/paginação.
          this.unir.casas = casas.map((c, i) => ({ ...c, _key: i }))
          if (onOk) onOk()
        })
        .catch(err => { console.log(err); toast.error('Erro ao buscar casas no SAP.') })
        .finally(() => loading.close())
    },
    confirmarUnir () {
      if (!this.casaSel) return
      const casa = this.casaSel
      loading.push()
      ObraVinculoSapRepository.UnirCasa(casa)
        .then(response => {
          const vinculo = (response.data && response.data.data) ? response.data.data : null
          if (!vinculo || !vinculo.id) throw new Error('vínculo sem id')
          return ObraVinculoSapRepository.VincularQma(this.qmaId, vinculo.id)
        })
        .then(() => {
          toast.success('QMA unido à casa do SAP.')
          this.$refs.modalUnirSap.hide()
          this.$emit('united', this.qmaId)
        })
        .catch(err => { console.log(err); toast.error('Erro ao unir QMA com a casa.') })
        .finally(() => loading.close())
    },
    desvincular () {
      if (!this.casaVinculada) return
      loading.push()
      ObraVinculoSapRepository.DesvincularQma(this.qmaId)
        .then(() => {
          toast.success('QMA desvinculado da casa.')
          this.casaSel = null
          this.$emit('united', this.qmaId)
          this.carregar()
        })
        .catch(err => { console.log(err); toast.error('Erro ao desvincular QMA.') })
        .finally(() => loading.close())
    }
  }
}
</script>

<style scoped>
.vinculo-atual {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  margin-bottom: 12px;
  background: #f1f9f4;
  border: 1px solid #bfe3cc;
  border-radius: 6px;
}
.vinculo-info { min-width: 0; }
.btn-remover {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  line-height: 1;
  font-size: 22px;
  font-weight: bold;
  color: #fff;
  background: #c0392b;
  border: none;
  border-radius: 50%;
  cursor: pointer;
}
.btn-remover:hover { background: #7d1f15; }
.lista-casas {
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
}
.casa-row {
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  font-family: 'Segoe UI';
}
.casa-row:last-child { border-bottom: none; }
.casa-row:hover { background-color: #f5f9f6; }
.casa-row-sel { background-color: #e8f5e9; }
.casa-row-sel:hover { background-color: #e8f5e9; }
.casa-main { font-size: 14px; font-weight: 600; color: #1a1818; }
.casa-sub { font-size: 12px; color: #5c6066; margin-top: 2px; }
.filtros-texto {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 8px;
}
</style>



// WEBPACK FOOTER //
// src/views/private/qma/inqueryQma/components/modalUnirSap/modalUnirSap.vue